import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useParams } from "react-router";
import type {
  OrganizationsBrandsLookupBrandItem,
  OrganizationsBrandsLookupItem,
} from "@src/domain/dto/organizationsBrands";
import { useGlobalDataLookup } from "@src/presentation/providers/GlobalDataLookupProvider";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import { clearMasterDataListCache } from "@src/presentation/providers/masterData/masterDataListCache";
import { findOrgBrandByKeys } from "./utils/findOrgBrandByKeys";
import {
  isMasterDataSection,
  type MasterDataSection,
} from "./masterDataNav.config";

export interface MasterDataBrandContextValue {
  organizationKey: string | null;
  brandKey: string | null;
  section: MasterDataSection | null;
  organization: OrganizationsBrandsLookupItem | null;
  brand: OrganizationsBrandsLookupBrandItem | null;
  isBrandSelected: boolean;
  isLookupLoading: boolean;
  isLookupReady: boolean;
  isInvalidBrandSelection: boolean;
}

const MasterDataBrandContext =
  createContext<MasterDataBrandContextValue | null>(null);

interface MasterDataBrandProviderProps {
  children: ReactNode;
}

export const MasterDataBrandProvider = ({
  children,
}: MasterDataBrandProviderProps) => {
  const { organizationKey, brandKey, section } = useParams<{
    organizationKey?: string;
    brandKey?: string;
    section?: string;
  }>();
  const {
    organizationsBrandsLookup,
    organizationsBrandsLookupLoading,
    refetchMasterDataAdjustmentCategoriesLookup,
    refetchMasterDataDeductionPrioritiesLookup,
    refetchMasterDataDeductionStagesLookup,
    refetchMasterDataDeductionValiditiesLookup,
    refetchMasterDataDeductionValidityRcasLookup,
  } = useGlobalDataLookup();

  // Organizations-style: drop list cache when leaving Master Data entirely.
  useEffect(() => {
    return () => {
      clearMasterDataListCache();
    };
  }, []);

  useEffect(() => {
    const key = brandKey?.trim() || null;
    MasterDataGraphQlAdapter.getInstance().setBrandKey(key);
    void Promise.all([
      refetchMasterDataAdjustmentCategoriesLookup(key),
      refetchMasterDataDeductionPrioritiesLookup(key),
      refetchMasterDataDeductionStagesLookup(key),
      refetchMasterDataDeductionValiditiesLookup(key),
      refetchMasterDataDeductionValidityRcasLookup(key),
    ]);

    return () => {
      MasterDataGraphQlAdapter.getInstance().setBrandKey(null);
      void Promise.all([
        refetchMasterDataAdjustmentCategoriesLookup(null),
        refetchMasterDataDeductionPrioritiesLookup(null),
        refetchMasterDataDeductionStagesLookup(null),
        refetchMasterDataDeductionValiditiesLookup(null),
        refetchMasterDataDeductionValidityRcasLookup(null),
      ]);
    };
  }, [
    brandKey,
    refetchMasterDataAdjustmentCategoriesLookup,
    refetchMasterDataDeductionPrioritiesLookup,
    refetchMasterDataDeductionStagesLookup,
    refetchMasterDataDeductionValiditiesLookup,
    refetchMasterDataDeductionValidityRcasLookup,
  ]);

  const value = useMemo<MasterDataBrandContextValue>(() => {
    const orgKey = organizationKey ?? null;
    const brKey = brandKey ?? null;
    const resolvedSection = isMasterDataSection(section) ? section : null;
    const isLookupReady =
      !organizationsBrandsLookupLoading && organizationsBrandsLookup != null;

    if (!orgKey || !brKey) {
      return {
        organizationKey: orgKey,
        brandKey: brKey,
        section: resolvedSection,
        organization: null,
        brand: null,
        isBrandSelected: false,
        isLookupLoading: organizationsBrandsLookupLoading,
        isLookupReady,
        isInvalidBrandSelection: false,
      };
    }

    const match =
      organizationsBrandsLookup == null
        ? null
        : findOrgBrandByKeys(organizationsBrandsLookup, orgKey, brKey);

    return {
      organizationKey: orgKey,
      brandKey: brKey,
      section: resolvedSection,
      organization: match?.organization ?? null,
      brand: match?.brand ?? null,
      isBrandSelected: Boolean(match),
      isLookupLoading: organizationsBrandsLookupLoading,
      isLookupReady,
      isInvalidBrandSelection: isLookupReady && !match,
    };
  }, [
    organizationKey,
    brandKey,
    section,
    organizationsBrandsLookup,
    organizationsBrandsLookupLoading,
  ]);

  return (
    <MasterDataBrandContext.Provider value={value}>
      {children}
    </MasterDataBrandContext.Provider>
  );
};

export function useMasterDataBrand(): MasterDataBrandContextValue {
  const context = useContext(MasterDataBrandContext);
  if (!context) {
    throw new Error(
      "useMasterDataBrand must be used within MasterDataBrandProvider",
    );
  }
  return context;
}
